import React, { useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Outlet, useNavigate, useParams } from 'react-router-dom';
import IdmlDropOverlay from '@/components/admin/PrintTextComparison/IdmlDropOverlay';
import usePageIdmlDrop from '@/components/admin/PrintTextComparison/usePageIdmlDrop';
import * as Routes from '@/modules/routes';
import NodeCrumbs from '../../shared/ProgramTree/NodeCrumbs';
import NodeTabs from '../../shared/ProgramTree/NodeTabs';
import { lessonPages } from '../../shared/ProgramTree/contentPages';
import { useProgramSummary } from '../../shared/ProgramTree/usePrograms';
import SPAButton from '../../shared/SPAButton';
import SPADataTable from '../../shared/SPADataTable';
import SPAPageHeader from '../../shared/SPAPageHeader';
import { useSPAUser } from '../../shared/SPAUser';
import { useSPAToast } from '../../shared/SPAToast';
import {
  lessonPrintToolsPath, lessonSectionsPath, programLessonsPath, unitEditPath, useContentMount,
} from '../contentMount';
import ProgramNavRail from '../ProgramLessons/ProgramNavRail';
import { stashIdml } from '../PrintTools/pendingIdml';
import LessonTagsPlaceholder from './LessonTagsPlaceholder';
import getSectionColumns from './sectionColumns';
import { LessonSectionRow, useLessonSectionRows, useReorderSections } from './useSections';

const sectionRowId = (section: LessonSectionRow) => section.id;
const sectionRowLabel = (section: LessonSectionRow) => section.full_title;

// AIDEV-NOTE: SPA recreation of the legacy sections page (/admin/lessons/:id/sections), the
// lesson's home. Rows drag to reorder, saved on drop. New Section stays on its legacy form and
// Edit Lesson is a tab like Edit Unit; the tags form is a placeholder for now (LessonTagsPlaceholder).
// An .idml dropped anywhere on the page goes to the print tools page and analyzes there, as the
// legacy page's tab flip did.
const LessonSectionsPage = () => {
  const { lessonId, programId } = useParams<{ lessonId: string; programId: string }>();
  const pid = Number(programId);
  const lid = Number(lessonId);
  const mount = useContentMount();
  const navigate = useNavigate();
  const { showPrintTools } = useSPAUser();
  const spaToast = useSPAToast();
  const {
    color, isLoading: isLoadingTitle, science, title: programTitle,
  } = useProgramSummary(pid);
  const sections = useLessonSectionRows(lid);
  const reorderSections = useReorderSections(pid, lid);
  const columns = useMemo(() => getSectionColumns(pid, lid), [pid, lid]);
  const dragging = usePageIdmlDrop((idml) => {
    stashIdml(idml);
    navigate(lessonPrintToolsPath(pid, lid, mount));
  }, showPrintTools);

  const onReorder = (rows: LessonSectionRow[]) => reorderSections(rows)
    .then(() => spaToast.success('Saved section order.'));

  return (
    <div className="tw-min-h-screen tw-bg-spa-bg tw-text-spa-on-bg">
      {dragging && createPortal(<IdmlDropOverlay />, document.body)}
      {mount === 'spa' && (
        <ProgramNavRail
          isLoadingTitle={isLoadingTitle}
          lessonId={lid}
          programColor={color}
          programId={pid}
          programTitle={programTitle}
        />
      )}
      <div className="tw-mx-auto tw-max-w-[1400px] tw-px-6 tw-py-4">
        <NodeCrumbs
          lessonId={lid}
          programId={pid}
          programTitle={programTitle}
          toLesson={id => lessonSectionsPath(pid, id, mount)}
          toProgram={id => programLessonsPath(id, mount)}
          toUnit={id => unitEditPath(pid, id, mount)}
        />
        <SPAPageHeader
          actions={(
            <SPAButton asChild size="md" variant="primary">
              <a href={Routes.new_admin_lesson_section_path(lid, { program_id: pid })}>New Section</a>
            </SPAButton>
          )}
          title="Sections"
        />
        <div className="tw-mb-5 tw-border-0 tw-border-b tw-border-solid tw-border-spa-border">
          <NodeTabs label="Lesson pages" pages={lessonPages(lid, pid, science, showPrintTools)} />
        </div>

        <LessonTagsPlaceholder />

        {sections.isError ? (
          <p className="tw-m-0 tw-text-sm tw-text-spa-error" role="alert">
            Couldn’t load the sections. Reload the page to try again.
          </p>
        ) : (
          <SPADataTable
            columns={columns}
            data={sections.data ?? []}
            defaultSort={[]}
            emptyMessage="No sections present for this lesson."
            isLoading={sections.isLoading}
            loadingRows={4}
            reorder={{ getRowId: sectionRowId, getRowLabel: sectionRowLabel, onReorder }}
          />
        )}
      </div>
      <Outlet />
    </div>
  );
};

export default LessonSectionsPage;
